import { LongTermOwner, RevenueSource, RecOwnership, UnitDecimals, Unit, ValidationRangeType, ValidationRangeTypes } from '~/constants';
import { formatDollars, formatNumber } from '~/utils';
import { Project } from '~/types/project';

type ValidationFunction = (...args: any) => boolean;

export const validFloat = (value: any) => {
  return parseFloat(value) !== Number(value);
};

export const validEmail = (value: any) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return !emailRegex.test(value);
};

export const nonEmptyString = (value: any) => !value;
export const nonEmptyBoolean = (value: any) => value !== true && value !== false;
export const nonEmptyArray = (value: any) => !value?.length;
export const nonEmptyObject = (value: any) => !value || !Object.keys(value).length;

export const showingAllRevenueInfo = (validation: ValidationFunction = () => true) => {
  return (value: any, project: Project) => {
    return (
      project.longTermOwner === LongTermOwner.THIRD_PARTY &&
      validation(value, project)
    );
  };
};

export const areRecStateFieldsEnabled = (state: 'Illinois' | 'New Jersey', validation: ValidationFunction = () => true) => {
  return (value: any, project: Project) => {
    if (state === project.addressState && project.recOwnership === RecOwnership.INVESTOR) {
      return validation(value, project);
    }
  };
};

//require kWac for all Illinois projects
export const isIllinoisProject = (validation: ValidationFunction = () => true) => {
  return (value: any, project: Project) => {
    if (project.addressState === 'Illinois') {
      return validation(value, project);
    }
  };
};

//require kWac for IL SHINES program market quotes
//this is important because kWac field is hidden within RECS field logic for market quotes 
export const isIllinoisShinesProgram = (validation: ValidationFunction = () => true) => {
  return (value: any, project: Project) => {
    if (project.addressState === 'Illinois' && project.recOwnership === RecOwnership.INVESTOR && project?.recsData?.Illinois?.isShinesProgram === 'YES') {
      return validation(value, project);
    }
  };
};

export const isNewJerseyQuote = (validation: ValidationFunction = () => true) => {
  return (value: any, project: Project) => {
    if (project.addressState === 'New Jersey') {
      return validation(value, project);
    }
  };
};

export const showingExpensesTab = (validation: ValidationFunction = () => true) => {
  return (value: any, project: Project) => {
    return project.longTermOwner !== LongTermOwner.END_USER && validation(value, project);
  };
};

export const showingCustomerInfoFields = (validation: ValidationFunction = () => true) => {
  return (value: any, project: Project) => {
    return project.revenueSource !== RevenueSource.COMMUNITY_SOLAR_SUBS && validation(value, project);
  };
};

export const showingCustomerContractFields = (validation: ValidationFunction = () => true) => {
  return (value: any, project: Project) => {
    return (
      project.revenueSource !== RevenueSource.COMMUNITY_SOLAR_SUBS ||
      (project.revenueSource === RevenueSource.COMMUNITY_SOLAR_SUBS && project.revenueAssumptionsFlag)
    ) && validation(value, project);
  };
};

export const showingStorageOmOptions = (validation: ValidationFunction = () => true) => {
  return(value: any, project: Project) => {
    return project.hasStorage && project.storageOmFlag && validation(value, project);
  };
};

export const max = (maxValue: any) => (value: any) => {
  if (value >= maxValue) {
    return `Please enter a number less than ${formatNumber(maxValue)}.`;
  }
};

export const range = (min: any, max: any, type: ValidationRangeType, size?: any) => (value: any) => {
  const compareValue = value < min || value > max;

  if (type === ValidationRangeTypes.NUMBER && compareValue) {
    return `Please enter a number between ${formatNumber(min)} and ${formatNumber(max)}.`;
  }

  if (type === ValidationRangeTypes.DOLLARS && compareValue) {
      return `Please enter a value between ${formatDollars(min, { dec: UnitDecimals.DOLLARS })} and ${formatDollars(max, { dec: UnitDecimals.DOLLARS })}.`;
  }

  if (type === ValidationRangeTypes.PERCENT && compareValue) {
    return `Please enter a value between ${formatNumber(min, 0, { suffix: '%'})} and ${formatNumber(max, 0, { suffix: '%' })}.`;
  }
};

export const avoidedCostOfSolar = (min: any, max: any) => (value: any) => {
  const compareValue = value < min || value > max;
  if (compareValue) {
    return `Please enter a value between ${formatDollars(min, { dec: UnitDecimals.DOLLARS })} and ${formatDollars(max, { dec: UnitDecimals.DOLLARS })} per kWh.`;
  }
};

export const unitRange = (min: any, max: any, unit: string, size?: any, ) => (value: any) => {
  const totalMin = min * size * 1000;
  const totalMax = max * size * 1000;

  if (unit === Unit.TOTAL && (value < totalMin || value > totalMax)) {
    return 'Budget outside expected range - please confirm unit selection.';
  }

  if (unit === Unit.WDC && (value < min || value > max)) {
    return `Please enter a value between ${formatDollars(min, { dec: UnitDecimals.DOLLARS })} and ${formatDollars(max, { dec: UnitDecimals.DOLLARS })}.`;
  }
};

export const stringLength = (num: any) => (value: any) => {
  if (value?.length !== num) {
    return `Please enter ${num} characters.`;
  }
};

export const validationChain = (...validationFns: any) => (value: any, project: Project) => {  
  let result;
  for (let i = 0; i < validationFns.length; i++) {
    const validationFn = validationFns[i];
    if (typeof validationFn === 'function') {
      result = validationFn(value, project);
    }
    if (result) break;
  }
  return result;
};
